import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Alert,
} from '@mui/material';
import Layout from '../components/Layout';
import { colors } from '@budgit/design-tokens';
import { buildDownloadDestinations, readAppStoreProviderToken } from '../services/storeNavigation';

/**
 * Referral invite landing for /reset/:code (#1330).
 *
 * Sunday Money Reset ritual was removed. This page only:
 * - Stashes pendingReferralCode for post-signup redeem
 * - Offers store / app open (budgit://r/CODE generic referral — not a ritual route)
 * - Does **not** set pendingRitual or open an in-app Sunday Reset surface
 */
const RitualReset = () => {
  const { code: rawCode } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = useMemo(
    () => (rawCode || '').trim().split(/[?#/]/)[0].toUpperCase(),
    [rawCode]
  );
  const [triedApp, setTriedApp] = useState(false);

  useEffect(() => {
    if (!code) return;
    try {
      sessionStorage.setItem('pendingReferralCode', code);
      // Clear any legacy Sunday Reset ritual flag from older pages.
      sessionStorage.removeItem('pendingRitual');
      const utmKeys = [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_content',
        'utm_term',
      ];
      const utm = {};
      utmKeys.forEach((k) => {
        const v = searchParams.get(k);
        if (v) utm[k] = v;
      });
      if (Object.keys(utm).length) {
        sessionStorage.setItem('utm_params', JSON.stringify(utm));
      }
    } catch {
      // private mode / blocked storage — continue without persistence
    }

    const ua = navigator.userAgent || '';
    const isMobile = /android|iphone|ipad|ipod/i.test(ua);
    if (isMobile && !triedApp) {
      setTriedApp(true);
      // Generic referral scheme — not budgit://reset (no ritual destination).
      window.location.href = `budgit://r/${code}`;
    }
  }, [code, searchParams, triedApp]);

  if (!code) {
    return (
      <Layout>
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Alert severity="warning">This invite link is missing a code.</Alert>
        </Container>
      </Layout>
    );
  }

  const destinations = buildDownloadDestinations(searchParams.toString(), {
    providerToken: readAppStoreProviderToken(),
  });
  const storeUrl = /android/i.test(navigator.userAgent || '')
    ? destinations.android
    : destinations.ios;

  return (
    <Layout>
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, color: colors.secondary }}>
            You&apos;re invited to Budgit
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            A friend shared Budgit with you. Get the app and we&apos;ll apply their
            invite when you sign up.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
            <Button
              variant="contained"
              size="large"
              href={`budgit://r/${code}`}
              sx={{
                backgroundColor: colors.primary,
                color: colors.secondary,
                fontWeight: 700,
                '&:hover': { backgroundColor: colors.primary, opacity: 0.92 },
              }}
            >
              Open in Budgit
            </Button>
            <Button
              variant="outlined"
              size="large"
              href={storeUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Get the app
            </Button>
            <Button
              variant="text"
              size="large"
              onClick={() => navigate('/')}
            >
              Continue in browser
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Invite code: <strong>{code}</strong>
          </Typography>
        </Paper>
      </Container>
    </Layout>
  );
};

export default RitualReset;
